/**
 * Getypeerde toegang tot `/channels`: koppelingen met externe kanalen, hun
 * importrondes en de conflicten die daaruit komen.
 *
 * De gedeelde uitwisseling staat in `http.ts`; hier staat alleen wat over kanalen
 * gaat. Twee dingen die dat zijn:
 *
 * 1. **De export-URL is een geheim en wordt als zodanig behandeld.** Wie hem heeft,
 *    ziet de bezette periodes van een woning zonder in te loggen. Het scherm toont
 *    hem daarom pas op verzoek, en er staat een knop naast om hem ongeldig te
 *    maken.
 * 2. **Een mislukte import is geen fout van de aanroep.** Dat een kanaal niet
 *    antwoordt, is informatie voor de verhuurder; `POST .../import` geeft dan
 *    gewoon 200 met `ok: false` in de uitkomst. Een client die dat als
 *    HTTP-fout zou behandelen, zou de reden weggooien die er juist toe doet.
 */
import {
  call,
  isRecord,
  readCount,
  readFlag,
  readOptionalText,
  readText,
  readTimestamp,
} from './http'
import { SPACE_KINDS } from './spaces-client'
import type { SpaceKind } from './spaces-client'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type ChannelFailure = Failure
export type ChannelResult<T> = Result<T>

/** Kanalen uit de CHECK van migratie 0016. */
export const CHANNELS = ['airbnb', 'booking', 'expedia', 'vrbo', 'other'] as const
export type Channel = (typeof CHANNELS)[number]

export const CONNECTION_STATUSES = ['active', 'paused', 'error'] as const
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number]

export const CONFLICT_RESOLUTIONS = ['kept_local', 'kept_channel', 'manual'] as const
export type ConflictResolution = (typeof CONFLICT_RESOLUTIONS)[number]

export interface ChannelConnection {
  readonly id: string
  readonly residenceId: string
  readonly channel: Channel
  readonly label: string
  readonly importUrl: string
  readonly exportUrl: string
  readonly status: ConnectionStatus
  readonly importEnabled: boolean
  readonly exportEnabled: boolean
  readonly lastImportAt: string | null
  readonly lastImportOk: boolean | null
  readonly lastError: string
  readonly failureStreak: number
}

export interface ChannelConflict {
  readonly id: string
  readonly connectionId: string
  readonly residenceId: string
  readonly channel: Channel
  readonly externalUid: string
  readonly checkIn: string
  readonly checkOut: string
  readonly conflictingStayId: string | null
  readonly detectedAt: string
  readonly resolvedAt: string | null
  readonly resolution: string
}

export interface ImportOutcome {
  readonly ok: boolean
  readonly eventsSeen: number
  readonly staysCreated: number
  readonly staysRemoved: number
  readonly conflicts: number
  readonly skipped: number
  readonly error: string
}

export interface CreateConnectionBody {
  readonly residenceId: string
  readonly channel: Channel
  readonly label: string
  readonly importUrl: string
  readonly exportEnabled: boolean
}

export interface UpdateConnectionBody {
  readonly label?: string
  readonly importUrl?: string
  readonly status?: ConnectionStatus
  readonly importEnabled?: boolean
  readonly exportEnabled?: boolean
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

function isChannel(value: unknown): value is Channel {
  const known: readonly string[] = CHANNELS
  return typeof value === 'string' && known.includes(value)
}

function isConnectionStatus(value: unknown): value is ConnectionStatus {
  const known: readonly string[] = CONNECTION_STATUSES
  return typeof value === 'string' && known.includes(value)
}

/**
 * Een koppeling uit het antwoord.
 *
 * Streng op wat dragend is — identiteit, kanaal, status — en mild op wat
 * cosmetisch is. Een koppeling zonder herkenbaar kanaal wordt overgeslagen in
 * plaats van als "other" getoond: dat zou een gok zijn over waar de agenda van een
 * woning vandaan komt.
 */
function readConnection(value: unknown): ChannelConnection | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const residenceId = readText(value, 'residenceId')
  const channel = value['channel']
  const status = value['status']
  if (id === '' || residenceId === '' || !isChannel(channel) || !isConnectionStatus(status)) {
    return null
  }

  const lastImportOk = value['lastImportOk']
  return {
    id,
    residenceId,
    channel,
    status,
    label: readText(value, 'label'),
    importUrl: readText(value, 'importUrl'),
    exportUrl: readText(value, 'exportUrl'),
    importEnabled: readFlag(value, 'importEnabled'),
    exportEnabled: readFlag(value, 'exportEnabled'),
    lastImportAt: readTimestamp(value, 'lastImportAt'),
    lastImportOk: typeof lastImportOk === 'boolean' ? lastImportOk : null,
    lastError: readText(value, 'lastError'),
    failureStreak: readCount(value, 'failureStreak'),
  }
}

function readConflict(value: unknown): ChannelConflict | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const checkIn = readText(value, 'checkIn')
  const checkOut = readText(value, 'checkOut')
  const channel = value['channel']
  if (id === '' || !CALENDAR_DATE.test(checkIn) || !CALENDAR_DATE.test(checkOut)) {
    return null
  }

  return {
    id,
    connectionId: readText(value, 'connectionId'),
    residenceId: readText(value, 'residenceId'),
    channel: isChannel(channel) ? channel : 'other',
    externalUid: readText(value, 'externalUid'),
    checkIn,
    checkOut,
    conflictingStayId: readOptionalText(value, 'conflictingStayId'),
    detectedAt: readTimestamp(value, 'detectedAt') ?? '',
    resolvedAt: readTimestamp(value, 'resolvedAt'),
    resolution: readText(value, 'resolution'),
  }
}

function readConnectionList(payload: unknown): ChannelConnection[] | null {
  if (!isRecord(payload) || !Array.isArray(payload['connections'])) {
    return null
  }
  const connections: ChannelConnection[] = []
  for (const entry of payload['connections']) {
    const connection = readConnection(entry)
    if (connection !== null) {
      connections.push(connection)
    }
  }
  return connections
}

function readOneConnection(payload: unknown): ChannelConnection | null {
  return isRecord(payload) ? readConnection(payload['connection']) : null
}

export async function listConnections(
  residenceId: string,
  options: RequestOptions = {},
): Promise<ChannelResult<ChannelConnection[]>> {
  return call(
    'GET',
    `/channels/connections?residenceId=${encodeURIComponent(residenceId)}`,
    undefined,
    readConnectionList,
    options,
  )
}

export async function createConnection(
  body: CreateConnectionBody,
): Promise<ChannelResult<ChannelConnection>> {
  return call('POST', '/channels/connections', body, readOneConnection)
}

export async function updateConnection(
  id: string,
  body: UpdateConnectionBody,
): Promise<ChannelResult<ChannelConnection>> {
  return call('PATCH', `/channels/connections/${encodeURIComponent(id)}`, body, readOneConnection)
}

export async function removeConnection(id: string): Promise<ChannelResult<true>> {
  return call('DELETE', `/channels/connections/${encodeURIComponent(id)}`, undefined, () => true)
}

export async function rotateExportToken(id: string): Promise<ChannelResult<ChannelConnection>> {
  return call(
    'POST',
    `/channels/connections/${encodeURIComponent(id)}/rotate-token`,
    undefined,
    readOneConnection,
  )
}

export async function importNow(id: string): Promise<ChannelResult<ImportOutcome>> {
  return call(
    'POST',
    `/channels/connections/${encodeURIComponent(id)}/import`,
    undefined,
    (payload) => {
      if (!isRecord(payload) || !isRecord(payload['result'])) {
        return null
      }
      const result = payload['result']
      return {
        ok: readFlag(result, 'ok'),
        eventsSeen: readCount(result, 'eventsSeen'),
        staysCreated: readCount(result, 'staysCreated'),
        staysRemoved: readCount(result, 'staysRemoved'),
        conflicts: readCount(result, 'conflicts'),
        skipped: readCount(result, 'skipped'),
        error: readText(result, 'error'),
      }
    },
  )
}

export async function listConflicts(
  options: RequestOptions = {},
): Promise<ChannelResult<ChannelConflict[]>> {
  return call(
    'GET',
    '/channels/conflicts',
    undefined,
    (payload) => {
      if (!isRecord(payload) || !Array.isArray(payload['conflicts'])) {
        return null
      }
      const conflicts: ChannelConflict[] = []
      for (const entry of payload['conflicts']) {
        const conflict = readConflict(entry)
        if (conflict !== null) {
          conflicts.push(conflict)
        }
      }
      return conflicts
    },
    options,
  )
}

export async function resolveConflict(
  id: string,
  resolution: ConflictResolution,
): Promise<ChannelResult<ChannelConflict>> {
  return call(
    'POST',
    `/channels/conflicts/${encodeURIComponent(id)}/resolve`,
    { resolution },
    (payload) => (isRecord(payload) ? readConflict(payload['conflict']) : null),
  )
}

// ---------------------------------------------------------------------------
// Exportpakket
// ---------------------------------------------------------------------------

export interface PackField {
  readonly field: string
  readonly value: string
  readonly length: number
  readonly limit: number
  /** Hoeveel tekens er te veel zijn; 0 wanneer het past. */
  readonly excess: number
}

export interface PackImage {
  readonly assetId: string
  readonly position: number
  readonly isPrimary: boolean
  readonly url: string
  readonly width: number
  readonly height: number
  readonly tooSmall: boolean
  readonly altText: string
  readonly caption: string
  /** De kamer waar deze foto bij hoort; `null` wanneer hij niet is ingedeeld. */
  readonly space: PackImageSpace | null
}

export interface PackImageSpace {
  readonly id: string
  readonly kind: SpaceKind
  readonly ordinal: number
  readonly label: string
}

export interface ExportPack {
  readonly residenceId: string
  readonly channel: Channel
  readonly locale: string
  readonly minImageWidth: number
  readonly maxImages: number
  readonly limitSource: string
  readonly fields: readonly PackField[]
  readonly images: readonly PackImage[]
  readonly imagesOmitted: number
}

function readField(value: unknown): PackField | null {
  if (!isRecord(value)) {
    return null
  }
  const field = readText(value, 'field')
  if (field === '') {
    return null
  }
  return {
    field,
    value: readText(value, 'value'),
    length: readCount(value, 'length'),
    limit: readCount(value, 'limit'),
    excess: readCount(value, 'excess'),
  }
}

function readImage(value: unknown): PackImage | null {
  if (!isRecord(value)) {
    return null
  }
  const assetId = readText(value, 'assetId')
  const url = readText(value, 'url')
  if (assetId === '' || url === '') {
    return null
  }
  return {
    assetId,
    url,
    position: readCount(value, 'position'),
    isPrimary: readFlag(value, 'isPrimary'),
    width: readCount(value, 'width'),
    height: readCount(value, 'height'),
    tooSmall: readFlag(value, 'tooSmall'),
    altText: readText(value, 'altText'),
    caption: readText(value, 'caption'),
    space: readImageSpace(value['space']),
  }
}

function readImageSpace(value: unknown): PackImageSpace | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const kind = value['kind']
  const known: readonly string[] = SPACE_KINDS
  // Een onbekende soort wordt als "niet ingedeeld" behandeld en niet als 'other':
  // dat laatste zou een indeling suggereren die er niet is.
  if (id === '' || typeof kind !== 'string' || !known.includes(kind)) {
    return null
  }
  return {
    id,
    kind: kind as SpaceKind,
    ordinal: readCount(value, 'ordinal'),
    label: readText(value, 'label'),
  }
}

export async function fetchExportPack(
  residenceId: string,
  channel: Channel,
  locale: string,
  options: RequestOptions = {},
): Promise<ChannelResult<ExportPack>> {
  const query = new URLSearchParams({ channel, locale })
  return call(
    'GET',
    `/channels/export-pack/${encodeURIComponent(residenceId)}?${query.toString()}`,
    undefined,
    (payload) => {
      if (!isRecord(payload) || !isRecord(payload['pack'])) {
        return null
      }
      const pack = payload['pack']
      const limits = isRecord(pack['limits']) ? pack['limits'] : {}

      const fields: PackField[] = []
      if (Array.isArray(pack['fields'])) {
        for (const entry of pack['fields']) {
          const field = readField(entry)
          if (field !== null) {
            fields.push(field)
          }
        }
      }

      const images: PackImage[] = []
      if (Array.isArray(pack['images'])) {
        for (const entry of pack['images']) {
          const image = readImage(entry)
          if (image !== null) {
            images.push(image)
          }
        }
      }

      const target = pack['channel']
      return {
        residenceId: readText(pack, 'residenceId'),
        channel: isChannel(target) ? target : 'other',
        locale: readText(pack, 'locale'),
        minImageWidth: readCount(limits, 'minImageWidth'),
        maxImages: readCount(limits, 'maxImages'),
        limitSource: readText(limits, 'source'),
        fields,
        images,
        imagesOmitted: readCount(pack, 'imagesOmitted'),
      }
    },
    options,
  )
}

/**
 * Vertaalsleutel bij een mislukte aanroep.
 *
 * `forbidden` staat er apart in en dat is geen formaliteit: de kanaalroutes zijn
 * `RequiresWrite`, óók om te lezen, omdat het antwoord de export-URL bevat. Een
 * `staff`- of `readonly`-gebruiker krijgt hier dus 403, en die hoort te lezen
 * waarom in plaats van "het lukt nu niet".
 */
export function channelErrorMessageKey(failure: ChannelFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.channels.error.rejected'
    case 'not_found':
      return 'dashboard.channels.error.notFound'
    case 'forbidden':
      return 'dashboard.channels.error.forbidden'
    case 'conflict':
      return 'dashboard.channels.error.conflict'
    case 'rate_limited':
      return 'dashboard.channels.error.rateLimited'
    default:
      return 'dashboard.channels.error.unavailable'
  }
}
